import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ActionButtons from '../components/ActionButtons';
import Header from '../components/Header';
import Loader from '../components/Loader';
import { fields, LOCAL_STORAGE_KEY } from '../constants';
import { getDetailsApi } from '../utils/api';
import { mergeLookupDetails } from '../utils/lookupDetails';

const VEHICLE_CONFIG = {
  passengerClasses: ["MOTOR CAB", "MAXI CAB", "BUS"],
  goodsClasses: ["GOODS CARRIER"],
  transportClasses: [
    "MOTOR CAB",
    "MAXI CAB",
    "BUS",
    "GOODS CARRIER",
    "ARTICULATED VEHICLE",
    "MOBILE WORKSHOP",
    "MOBILE CANTEEN",
    "CAMPER VAN / TRAILER",
    "CASH VAN",
    "FIRE TENDERS",
    "SNORKED LADDERS",
    "AUXILIARY TRAILER",
    "FIRE FIGHTING VEHICLE",
    "EXCAVATOR (COMMERCIAL)",
  ],
  nonTransportClasses: [
    "FORK LIFT",
    "VEHICLE FITTED WITH RIG",
    "VEHICLE FITTED WITH GENERATOR",
    "VEHICLE FITTED WITH COMPRESSOR",
    "CRANE MOUNTED VEHICLE",
    "TOW TRUCK",
    "BREAKDOWN VAN",
    "RECOVERY VEHICLE",
    "TOWER WAGON",
    "TREE TRIMMING VEHICLE",
    "CONSTRUCTION EQUIPMENT VEHICLE",
  ],
};

const UP_DISTRICTS = [
  "SAHARANPUR",
  "BIJNOR",
  "RAMPUR",
  "BAREILLY",
  "PILIBHIT",
  "LAKHIMPUR",
  "BAHRAICH",
  "SHRAVASTI",
  "BALRAMPUR",
  "SIDDHARTH NAGAR",
  "MAHARAJGANJ",
  "KUSHINAGAR",
  "DEORIA",
  "BALLIA",
  "GHAZIPUR",
  "CHANDAULI",
  "SONBHADRA",
  "MIRZAPUR",
  "PRAYAGRAJ",
  "CHITRAKOOT",
  "BANDA",
  "HAMIRPUR",
  "MAHOBA",
  "JHANSI",
  "LALITPUR",
  "ORAI",
  "ETAWAH",
  "AGRA",
  "MATHURA",
  "ALIGARH",
  "GAUTAM BUDDHA NAGAR",
  "GHAZIABAD",
  "BAGHPAT",
  "SHAMLI",
];

const SERVICE_TYPES = ["ORDINARY", "AIR CONDITIONED", "NOT APPLICABLE"];
const TAX_MODES = ["DAYS", "WEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"];
const PERMIT_TYPES = ["NOT APPLICABLE", "TEMPORARY PERMIT", "TOURIST PERMIT"];
const VEHICLE_TYPES = ["TRANSPORT", "NON-TRANSPORT"];

const Up = () => {
  const isLoggedIn = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || 'null');
  const state = 'up';
  const history = useHistory();
  const [payLoad, setPayLoad] = useState({
    vehicleNo: '',
    chassisNo: '',
    mobileNo: '',
    vehiclePermitType: '', // This will be used as "Vehicle Type"
    vehicleClass: '',
    vehicleCategory: '',
    seatingCapacityExcludingDriver: '',
    sleeperCap: '',
    grossVehicleWeight: '',
    unladenWeight: '',
    borderBarrier: '', // This will be used as "Entry District Name"
    checkpostName: '', // This will be used as "Entry Checkpost Name"
    permitType: '',
    permitUpto: '',
    permitNumber: '',
    serviceType: '',
    taxMode: '',
    taxFromDate: '',
    taxUptoDate: '',
    totalAmount: '',
    ownerName: '',
    fromState: '',
    mvTax: '',
    insuranceValidity: '',
    fitnessValidity: '',
    puccValidity: '',
    roadTaxValidity: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const form = useRef(null);

  // Vehicle Class Options based on Vehicle Type (vehiclePermitType)
  const vehicleClassOptions = useMemo(() => {
    if (payLoad.vehiclePermitType === 'TRANSPORT') {
      return VEHICLE_CONFIG.transportClasses;
    }
    if (payLoad.vehiclePermitType === 'NON-TRANSPORT') {
      return VEHICLE_CONFIG.nonTransportClasses;
    }
    return [];
  }, [payLoad.vehiclePermitType]);

  // Vehicle Category Options based on Vehicle Class
  const vehicleCategoryOptions = useMemo(() => {
    const cls = payLoad.vehicleClass;
    if (!cls) return [];

    if (VEHICLE_CONFIG.passengerClasses.includes(cls)) {
      return ['LIGHT PASSENGER VEHICLE', 'MEDIUM PASSENGER VEHICLE', 'HEAVY PASSENGER VEHICLE'];
    }
    if (VEHICLE_CONFIG.goodsClasses.includes(cls)) {
      return ['LIGHT GOODS VEHICLE', 'MEDIUM GOODS VEHICLE', 'HEAVY GOODS VEHICLE'];
    }
    return ['LIGHT MOTOR VEHICLE', 'MEDIUM MOTOR VEHICLE', 'HEAVY MOTOR VEHICLE'];
  }, [payLoad.vehicleClass]);

  // Sync Entry District and Entry Checkpost
  useEffect(() => {
    if (payLoad.borderBarrier) {
      setPayLoad((prev) => ({ ...prev, checkpostName: prev.borderBarrier }));
    }
  }, [payLoad.borderBarrier]);

  // Conditional Visibility Logic
  const isPassengerType = VEHICLE_CONFIG.passengerClasses.includes(payLoad.vehicleClass);

  const getDetailsHandler = async () => {
    if (!payLoad.vehicleNo) {
      alert('Please enter vehicle no.');
      return;
    }
    setIsLoading(true);
    const { data } = await getDetailsApi({
      vehicleNo: payLoad.vehicleNo,
    });
    setIsLoading(false);
    if (data && data.success) {
setPayLoad((e) => ({
        ...e,
        ...mergeLookupDetails(e, data.detail),
      }));
    }
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const finalPayload = {
      ...payLoad,
      unladenWeight: payLoad.unladenWeight || 0,
    };
    history.push('/select-payment', {
      formData: {
        ...finalPayload,
        state,
      },
    });
  };

  const onResetHandler = () => {
    let p = {};
    Object.keys(payLoad).forEach((e) => {
      p[e] = '';
    });
    setPayLoad({ ...p });
  };

  const onCalculateTax = () => {
    if (!payLoad.taxFromDate || !payLoad.taxUptoDate) {
      alert('Please enter tax dates');
      return;
    }
    setPayLoad((prev) => ({
      ...prev,
      totalAmount: prev.mvTax || '',
    }));
  };

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setPayLoad((old) => {
      const updates = { [name]: value };
      
      // Reset dependent fields
      if (name === 'vehiclePermitType') {
        updates.vehicleClass = '';
        updates.vehicleCategory = '';
      }
      if (name === 'vehicleClass') {
        updates.vehicleCategory = '';
      }
      
      return { ...old, ...updates };
    });
  };

  // Sync total amount with mvTax
  useEffect(() => {
    setPayLoad((prev) => ({
      ...prev,
      totalAmount: prev.mvTax || '',
    }));
  }, [payLoad.mvTax]);

  // Helper to format date string to DD/MM/YYYY
  const formatDateForTable = (dateStr) => {
    if (!dateStr) return '';
    try {
      const [datePart] = dateStr.split('T');
      const [y, m, d] = datePart.split('-');
      return `${d}/${m}/${y}`;
    } catch (e) {
      return '';
    }
  };

  if (!isLoggedIn || !isLoggedIn.accessState || !isLoggedIn.accessState.includes(fields.stateName.up)) {
    return (
      <>
        <Header />
        <div className='container text-center mt-4 '>
          <h3>No Access of this state</h3>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className='text-center'>
        <p className='login-heading mt-4'>
          <b>BORDER TAX PAYMENT FOR ENTRY INTO</b> <span>UTTAR PRADESH</span>
        </p>
      </div>
      <div className='box box--main'>
        <div className='box__heading--blue'>Tax Payment Details</div>
        <form
          ref={form}
          onSubmit={onSubmitHandler}
          className='service-type tax-details mt-4'
        >
          <div className='row'>
            <div className='col-6'>
              {/* <!-- vehicle number --> */}
              <div className='form__control'>
                <label className='form__label d-block w-100 text-left' htmlFor='vehicleNo'>
                  Vehicle No.<sup>*</sup>
                </label>
                <input
                  required
                  maxLength='10'
                  tabIndex='1'
                  inputMode='text'
                  disabled={isLoading}
                  autoFocus
                  value={payLoad.vehicleNo}
                  onChange={onChangeHandler}
                  className='form__input w-100'
                  type='text'
                  id='vehicleNo'
                  name='vehicleNo'
                />
              </div>

              {/* <!-- chassis number --> */}
              <div className='form__control'>
                <label className='form__label d-block w-100 text-left' htmlFor='chassisNo'>
                  Chassis No.<sup>*</sup>
                </label>
                <input
                  required
                  tabIndex='2'
                  maxLength='19'
                  inputMode='text'
                  disabled={isLoading}
                  onChange={onChangeHandler}
                  value={payLoad.chassisNo}
                  className='form__input w-100'
                  type='text'
                  id='chassisNo'
                  name='chassisNo'
                />
              </div>

              {/* <!-- mobile number --> */}
              <div className='form__control'>
                <label className='form__label d-block w-100 text-left' htmlFor='mobileNo'>
                  Mobile No.<sup>*</sup>
                </label>
                <input
                  tabIndex='3'
                  inputMode='tel'
                  required
                  disabled={isLoading}
                  onChange={onChangeHandler}
                  placeholder='SMS about payment will be sent to this number '
                  className='form__input w-100'
                  type='text'
                  id='mobileNo'
                  value={payLoad.mobileNo}
                  maxLength='10'
                  minLength='10'
                  name='mobileNo'
                />
              </div>

              {/* <!-- vehicle type --> */}
              <div className='form__control'>
                <label className='form__label d-block w-100 text-left' htmlFor='vehiclePermitType'>
                  Vehicle Type<sup>*</sup>
                </label>
                <select
                  tabIndex='4'
                  required
                  disabled={isLoading}
                  value={payLoad.vehiclePermitType}
                  onChange={onChangeHandler}
                  name='vehiclePermitType'
                  id='vehiclePermitType'
                >
                  <option value=''>--Select Vehicle Type--</option>
                  {VEHICLE_TYPES.map((type) => {
                    return (
                      <option value={type} key={type}>
                        {type}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className='row'>
                {/* seating/exempt - conditional */}
                {isPassengerType ? (
                  <>
                    <div className='col-sm-6'>
                      <div className='form__control'>
                        <label className='form__label d-block w-100 text-left' htmlFor='seatingCapacityExcludingDriver'>
                          Seating Capacity(Ex. Driver)<sup>*</sup>
                        </label>
                        <input
                          min='0'
                          tabIndex='5'
                          required
                          inputMode='numeric'
                          disabled={isLoading}
                          onChange={onChangeHandler}
                          className='form__input w-100'
                          type='number'
                          value={payLoad.seatingCapacityExcludingDriver}
                          id='seatingCapacityExcludingDriver'
                          name='seatingCapacityExcludingDriver'
                        />
                      </div>
                    </div>

                    <div className='col-sm-6'>
                      <div className='form__control'>
                        <label className='form__label d-block w-100 text-left' htmlFor='sleeperCap'>
                          Sleeper Cap<sup>*</sup>
                        </label>
                        <input
                          required
                          tabIndex='6'
                          min='0'
                          inputMode='numeric'
                          disabled={isLoading}
                          onChange={onChangeHandler}
                          className='form__input w-100'
                          type='number'
                          value={payLoad.sleeperCap}
                          id='sleeperCap'
                          name='sleeperCap'
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className='col-sm-6'>
                      <div className='form__control'>
                        <label className='form__label d-block w-100 text-left' htmlFor='grossVehicleWeight'>
                          Gross Vehicle Wt.(in kg)<sup>*</sup>
                        </label>
                        <input
                          min='0'
                          tabIndex='5'
                          required
                          inputMode='numeric'
                          disabled={isLoading}
                          onChange={onChangeHandler}
                          className='form__input w-100'
                          type='number'
                          value={payLoad.grossVehicleWeight}
                          id='grossVehicleWeight'
                          name='grossVehicleWeight'
                        />
                      </div>
                    </div>

                    <div className='col-sm-6'>
                      <div className='form__control'>
                        <label className='form__label d-block w-100 text-left' htmlFor='unladenWeight'>
                          Unladen Wt.(in kg)<sup>*</sup>
                        </label>
                        <input
                          min='0'
                          tabIndex='5'
                          required
                          inputMode='numeric'
                          disabled={isLoading}
                          onChange={onChangeHandler}
                          className='form__input w-100'
                          type='number'
                          value={payLoad.unladenWeight}
                          id='unladenWeight'
                          name='unladenWeight'
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className='row'>
                <div className='col-sm-6'>
                  {/* border barrier / District */}
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='borderBarrier'>
                      Entry District Name<sup>*</sup>
                    </label>
                    <select
                      tabIndex='7'
                      required
                      disabled={isLoading}
                      value={payLoad.borderBarrier}
                      onChange={onChangeHandler}
                      name='borderBarrier'
                      id='borderBarrier'
                    >
                      <option value=''>--Select District--</option>
                      {UP_DISTRICTS.map((dist) => {
                        return (
                          <option key={dist} value={dist}>
                            {dist}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className='col-sm-6'>
                  {/* checkpost name */}
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='checkpostName'>
                      Entry Checkpost Name<sup>*</sup>
                    </label>
                    <select
                      tabIndex='7'
                      required
                      disabled={isLoading}
                      value={payLoad.checkpostName}
                      onChange={onChangeHandler}
                      name='checkpostName'
                      id='checkpostName'
                    >
                      <option value=''>--Select Checkpost--</option>
                      {UP_DISTRICTS.map((dist) => {
                        return (
                          <option key={dist} value={dist}>
                            {dist}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              <div className='row'>
                {/* Permit type */}
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='permitType'>
                      Permit Type<sup>*</sup>
                    </label>
                    <select
                      tabIndex='8'
                      disabled={isLoading}
                      required
                      onChange={onChangeHandler}
                      value={payLoad.permitType}
                      name='permitType'
                      id='permitType'
                    >
                      <option value=''>--Select Permit Type--</option>
                      {PERMIT_TYPES.map((type) => {
                        return (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* permit upto / validity */}
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='permitUpto'>
                      Permit Validity<sup>*</sup>
                    </label>
                    <input
                      tabIndex='9'
                      disabled={isLoading}
                      required
                      className='form__input w-100'
                      type='date'
                      value={payLoad.permitUpto}
                      onChange={onChangeHandler}
                      id='permitUpto'
                      name='permitUpto'
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                {/* Tax from */}
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='taxFromDate'>
                      Tax from<sup>*</sup>
                    </label>
                    <input
                      tabIndex='10'
                      disabled={isLoading}
                      required
                      className='form__input w-100'
                      type='datetime-local'
                      id='taxFromDate'
                      name='taxFromDate'
                      onChange={onChangeHandler}
                      value={payLoad.taxFromDate}
                    />
                  </div>
                </div>

                {/* Tax upto */}
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='taxUptoDate'>
                      Tax Upto<sup>*</sup>
                    </label>
                    <input
                      tabIndex='11'
                      required
                      disabled={isLoading}
                      className='form__input w-100'
                      type='datetime-local'
                      id='taxUptoDate'
                      name='taxUptoDate'
                      value={payLoad.taxUptoDate}
                      onChange={onChangeHandler}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* right side */}
            <div className='col-6'>
              <div className='form__control text-left'>
                <label className='form__label d-block w-100 text-left'>&nbsp;</label>
                {isLoading && <Loader className='loader__get-details' />}
                {!isLoading && (
                  <button
                    disabled={isLoading}
                    type='button'
                    onClick={getDetailsHandler}
                    className='box__button get-details'
                  >
                    <span className='glyphicon glyphicon-arrow-down mr-3'></span>
                    Get Details
                  </button>
                )}
              </div>

              {/* owner name */}
              <div className='form__control'>
                <label className='form__label d-block w-100 text-left' htmlFor='ownerName'>
                  Owner Name<sup>*</sup>
                </label>
                <input
                  tabIndex='12'
                  disabled={isLoading}
                  required
                  inputMode='text'
                  className='form__input w-100'
                  type='text'
                  id='ownerName'
                  name='ownerName'
                  onChange={onChangeHandler}
                  value={payLoad.ownerName}
                />
              </div>

              {/* from state */}
              <div className='form__control'>
                <label className='form__label d-block w-100 text-left' htmlFor='fromState'>
                  From State<sup>*</sup>
                </label>
<select
                  disabled={isLoading}
                  required
                  tabIndex='13'
                  value={payLoad.fromState}
                  onChange={onChangeHandler}
                  name='fromState'
                  id='fromState'
                >
                  <option value=''>--------Select State--------</option>
                  {fields.fromState.map((type) => {
                    return (
                      <option value={String(type.name).toUpperCase()} key={type.name}>
                        {type.name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className='row'>
                <div className='col-sm-6'>
                  {/* vehicle class */}
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='vehicleClass'>
                      Vehicle Class<sup>*</sup>
                    </label>
                    <select
                      required
                      tabIndex='14'
                      disabled={isLoading}
                      value={payLoad.vehicleClass}
                      onChange={onChangeHandler}
                      name='vehicleClass'
                      id='vehicleClass'
                    >
                      <option value=''>--Select Vehicle Class--</option>
                      {vehicleClassOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className='col-sm-6'>
                  {/* vehicle category */}
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='vehicleCategory'>
                      Vehicle Category<sup>*</sup>
                    </label>
                    <select
                      required
                      tabIndex='15'
                      disabled={isLoading}
                      value={payLoad.vehicleCategory}
                      onChange={onChangeHandler}
                      name='vehicleCategory'
                      id='vehicleCategory'
                    >
                      <option value=''>--Select Category--</option>
                      {vehicleCategoryOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className='row'>
                {/* Service Type */}
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='serviceType'>
                      Service type<sup>*</sup>
                    </label>
                    <select
                      tabIndex='16'
                      disabled={isLoading}
                      required
                      value={payLoad.serviceType}
                      onChange={onChangeHandler}
                      name='serviceType'
                      id='serviceType'
                    >
                      <option value=''>--Select Service Type--</option>
                      {SERVICE_TYPES.map((type) => {
                        return (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* Tax mode */}
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='taxMode'>
                      Tax Mode<sup>*</sup>
                    </label>
                    <select
                      tabIndex='17'
                      disabled={isLoading}
                      required
                      onChange={onChangeHandler}
                      value={payLoad.taxMode}
                      name='taxMode'
                      id='taxMode'
                    >
                      <option value=''>--Select Tax Mode--</option>
                      {TAX_MODES.map((type) => {
                        return (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='puccValidity'>
                      PUCC Validity<sup>*</sup>
                    </label>
                    <input
                      tabIndex='18'
                      disabled={isLoading}
                      required
                      className='form__input w-100'
                      type='date'
                      value={payLoad.puccValidity}
                      onChange={onChangeHandler}
                      id='puccValidity'
                      name='puccValidity'
                    />
                  </div>
                </div>
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='roadTaxValidity'>
                      Road Tax Validity<sup>*</sup>
                    </label>
                    <input
                      tabIndex='19'
                      disabled={isLoading}
                      required
                      className='form__input w-100'
                      type='date'
                      value={payLoad.roadTaxValidity}
                      onChange={onChangeHandler}
                      id='roadTaxValidity'
                      name='roadTaxValidity'
                    />
                  </div>
                </div>
              </div>

              <div className='form__control'>
                <label className='form__label d-block w-100 text-left' htmlFor='permitNumber'>
                  Permit Number<sup>*</sup>
                </label>
                <input
                  disabled={isLoading}
                  required
                  className='form__input w-100'
                  type='text'
                  tabIndex='20'
                  value={payLoad.permitNumber}
                  onChange={onChangeHandler}
                  id='permitNumber'
                  name='permitNumber'
                  inputMode='text'
                />
              </div>

              <div className='row'>
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='insuranceValidity'>
                      Insurance Validity<sup>*</sup>
                    </label>
                    <input
                      tabIndex='21'
                      disabled={isLoading}
                      required
                      className='form__input w-100'
                      type='date'
                      value={payLoad.insuranceValidity}
                      onChange={onChangeHandler}
                      id='insuranceValidity'
                      name='insuranceValidity'
                    />
                  </div>
                </div>
                <div className='col-sm-6'>
                  <div className='form__control'>
                    <label className='form__label d-block w-100 text-left' htmlFor='fitnessValidity'>
                      Fitness Validity<sup>*</sup>
                    </label>
                    <input
                      tabIndex='22'
                      disabled={isLoading}
                      required
                      className='form__input w-100'
                      type='date'
                      value={payLoad.fitnessValidity}
                      onChange={onChangeHandler}
                      id='fitnessValidity'
                      name='fitnessValidity'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* table */}
          <div className='row mt-3'>
            <div className='col-12'>
              <table className='hr-table'>
                <thead>
                  <tr>
                    <th className='hr-table-1'>SI. No.</th>
                    <th className='hr-table-2'>Particulars</th>
                    <th>Tax From</th>
                    <th>Tax Upto</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                      <td className='hr-table-body'>1</td>
                      <td className='hr-table-body'>
                        MV Tax<sup style={{ color: 'red' }}>*</sup>
                      </td>
                      <td className='hr-table-body'>{formatDateForTable(payLoad.taxFromDate)}</td>
                      <td className='hr-table-body'>{formatDateForTable(payLoad.taxUptoDate)}</td>
                      <td className='hr-table-body'>
                      <input
                        type='number'
                        className='form__input'
                        name='mvTax'
                        value={payLoad.mvTax}
                        onChange={onChangeHandler}
                        required
                        min='0'
                        inputMode='numeric'
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <br />
          {/* total */}
          <div className='row'>
            <div className='col-sm-6'>
              <div className='form__control'>
                <label className='form__label d-block w-100 text-left' htmlFor='totalAmount'>
                  Total amount<sup>*</sup>
                </label>
                <input
                  required
                  tabIndex='22'
                  min='0'
                  disabled={isLoading}
                  inputMode='numeric'
                  onChange={onChangeHandler}
                  value={payLoad.totalAmount}
                  className='form__input w-100'
                  type='number'
                  id='totalAmount'
                  name='totalAmount'
                />
              </div>
            </div>
            <div className='col-sm-6'>
              <label className='form__label d-block w-100 text-left'>&nbsp;</label>
              <ActionButtons
                tabIndex='23'
                isDisabled={isLoading}
                onReset={onResetHandler}
                onCalculateTax={onCalculateTax}
              />
            </div>
          </div>
        </form>
        <br />
      </div>
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
    </>
  );
};

export default Up;
